import { useEffect, useState } from 'react';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import useMapColorScheme from '../../hooks/useMapColorScheme';
import { geocodeAddressToLocation } from '../../utils/addressGeocoding';

// Gleiche Map-ID wie die übrigen Portal-Karten (AdvancedMarker braucht eine).
const MAP_ID = '8bb9c199f544a76';
const GERMANY = { lat: 51.1657, lng: 10.4515 };

const circleCenter = (locationBias) => {
  const center = locationBias?.center;
  if (!center) return null;
  const lat = typeof center.lat === 'function' ? center.lat() : center.lat;
  const lng = typeof center.lng === 'function' ? center.lng() : center.lng;
  return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null;
};

/**
 * Notausgang der Vorschlagspflicht (nextcab_mobile/docs/general/adressauswahl_konzept.md
 * §3.2): Für Orte, die Google nicht als Adresse kennt (Hinterhof, Neubaugebiet,
 * Bahnhof-Rückseite), setzt der Disponent den Punkt selbst. Der getippte Text
 * bleibt die Adresse, die Koordinate kommt vom Pin.
 *
 * Startpunkt: grobe Auflösung des Textes, sonst Mitte des Betriebsgebiets.
 */
const MapPinDialog = ({ open, address, locationBias, onClose, onConfirm }) => {
  const mapScheme = useMapColorScheme();
  const [center, setCenter] = useState(null);
  const [pin, setPin] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setPin(null);
    setCenter(null);
    (async () => {
      const guess = await geocodeAddressToLocation(address);
      if (cancelled) return;
      if (guess) {
        setCenter({ lat: guess.latitude, lng: guess.longitude, zoom: 17 });
      } else {
        const areaCenter = circleCenter(locationBias);
        setCenter(areaCenter ? { ...areaCenter, zoom: 12 } : { ...GERMANY, zoom: 6 });
      }
    })();
    return () => { cancelled = true; };
  }, [open, address, locationBias]);

  const handleMapClick = (event) => {
    const latLng = event.detail?.latLng;
    if (latLng) setPin({ lat: latLng.lat, lng: latLng.lng });
  };

  const handleDragEnd = (event) => {
    const latLng = event.latLng;
    if (latLng) setPin({ lat: latLng.lat(), lng: latLng.lng() });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Auf Karte festlegen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Klicken Sie auf die Stelle, an die der Fahrer fahren soll. Die Adresse
          {address ? ` „${address}"` : ''} bleibt als Text erhalten.
        </Typography>
        <Box sx={{ height: 420, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
          {center ? (
            <Map
              key={mapScheme.key}
              mapId={MAP_ID}
              colorScheme={mapScheme.colorScheme}
              defaultCenter={{ lat: center.lat, lng: center.lng }}
              defaultZoom={center.zoom}
              gestureHandling="greedy"
              disableDefaultUI={false}
              streetViewControl={false}
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%' }}
            >
              {pin && (
                <AdvancedMarker position={pin} draggable onDragEnd={handleDragEnd} />
              )}
            </Map>
          ) : (
            <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          disabled={!pin}
          onClick={() => onConfirm({ latitude: pin.lat, longitude: pin.lng })}
        >
          Punkt übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPinDialog;
